import React, { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Input,
    InputNumber,
    Menu,
    Popover,
    Select,
    Tooltip,
} from "antd";
import {
    AppstoreOutlined,
    BarChartOutlined,
    CloudOutlined,
    DeleteOutlined,
    DownOutlined,
    FileTextOutlined,
    HddOutlined,
    PlusOutlined,
    RightOutlined,
    SafetyOutlined,
    TeamOutlined,
    ToolOutlined,
    WifiOutlined,
} from "@ant-design/icons";
import { BudgetTabs } from "../layout/BudgetTabs";
import { PlaybookDrawer } from "./PlaybookDrawer";
import { hashSeed } from "../model/forecast";
import { useBudget } from "../BudgetContext";
import {
    availableYears,
    getLines,
    groupBySegment,
    tailPoolByCategory,
    tailVendorsForCategory,
    TailVendor,
} from "../model/api";
import {
    BudgetLine,
    CategoryKey,
    Confidence,
    IncreaseKind,
    Segment,
    Tone,
    usd,
} from "../model/types";
import { formatCompact, formatPct } from "../model/format";
import classes from "./OverviewView.module.less";

/** Close-quarter choices for new spend: the four quarters of the viewed year. */
const quarterOptions = (year: number): string[] =>
    [1, 2, 3, 4].map((q) => `Q${q} ${year}`);

/** Quarter number from a "Q3 2027" label; rows without one sort last. */
const quarterIndexOf = (label?: string): number => {
    const m = label?.match(/Q(\d)/);
    return m ? Number(m[1]) : 99;
};

const CONFIDENCE: Record<Confidence, { label: string; color: string }> = {
    high: { label: "High", color: "#389e0d" },
    medium: { label: "Medium", color: "#d46b08" },
    low: { label: "Low", color: "rgba(0, 0, 0, 0.55)" },
};

const confidenceTagFor = (line?: BudgetLine): React.ReactNode => {
    const level: Confidence =
        line?.segment === "under_contract" || line?.increaseKind === "capped"
            ? "high" // locked through the year, or capped renewal
            : line?.playbook
            ? "medium"
            : "low";
    return (
        <span className={classes.confText} style={{ color: CONFIDENCE[level].color }}>
            {CONFIDENCE[level].label}
        </span>
    );
};

/** Overall budget review phase. */
type Phase = "draft" | "under_review" | "approved";
const PHASE_LABEL: Record<Phase, string> = {
    draft: "Build",
    under_review: "Under review",
    approved: "Approved",
};
const PHASE_COLOR: Record<Phase, string> = {
    draft: "#1890ff",
    under_review: "#fa8c16",
    approved: "#52c41a",
};
const NEXT_PHASE: Record<Phase, Phase | null> = {
    draft: "under_review",
    under_review: "approved",
    approved: null,
};

type ReviewState = "pending" | "approved" | "flagged";
const REVIEW_OPTIONS: Array<{ label: string; value: ReviewState }> = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Flagged", value: "flagged" },
];

/** Segments reviewed only at the section level (no per-line widget). */
const SEGMENT_REVIEW: Segment[] = ["under_contract", "tail"];

const OWNERS = [
    "Dana Whitfield",
    "Marcus Lee",
    "Priya Nair",
    "Tom Becker",
    "Sofia Ramos",
    "Liam O'Connor",
    "Hana Kim",
    "Diego Alvarez",
    "Grace Park",
    "Noah Bennett",
];
const ownerFor = (id: string) => OWNERS[hashSeed(id) % OWNERS.length];
const CONFIDENCE_OPTIONS = (["high", "medium", "low"] as Confidence[]).map((c) => ({
    label: CONFIDENCE[c].label,
    value: c,
}));

const CATEGORY_ICON: Record<CategoryKey, React.ReactNode> = {
    saas: <AppstoreOutlined />,
    infra_cloud: <CloudOutlined />,
    hardware: <HddOutlined />,
    telecom: <WifiOutlined />,
    prof_services: <TeamOutlined />,
    security: <SafetyOutlined />,
    data_analytics: <BarChartOutlined />,
    support_maint: <ToolOutlined />,
};

const CATEGORY_COLOR: Record<CategoryKey, string> = {
    saas: "#6366f1",
    infra_cloud: "#0ea5e9",
    hardware: "#64748b",
    telecom: "#14b8a6",
    prof_services: "#f59e0b",
    security: "#ef4444",
    data_analytics: "#8b5cf6",
    support_maint: "#10b981",
};

const INCREASE_PILL: Record<IncreaseKind, string> = {
    capped: "Capped",
    market: "Market",
    usage: "Usage",
};

const ORDER: Segment[] = ["up_for_renewal", "new_spend", "tail", "under_contract"];
const TRACK_EXPANDED: Record<Segment, boolean> = {
    up_for_renewal: true,
    under_contract: false,
    tail: false,
    new_spend: true,
};
const ALL_EXPANDED: Record<Segment, boolean> = {
    up_for_renewal: true,
    under_contract: true,
    tail: true,
    new_spend: true,
};

const H1_FRACTION = 0.5;
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

/** Justify a tail category's trend % from (made-up) purchase history — used when
 * there's no contracted rate, only past POs. */
const tailTrendNote = (vendorCount: number, inc: number, seed: string): string => {
    const pos = 40 + (hashSeed(`${seed}:pos`) % 160); // 40–200 POs
    const cagr = Math.max(1, Math.round(inc * 100) - 1 + (hashSeed(`${seed}:cagr`) % 3));
    return `No contracted rate — trend from purchase history: ${pos} POs across ${vendorCount} vendor${
        vendorCount === 1 ? "" : "s"
    } in the last 12 months, spend up ~${Math.round(
        inc * 100
    )}% YoY (3-yr CAGR ${cagr}%). Carried forward.`;
};

/** Justify an under-contract usage % from (made-up) seat/license growth. */
const usageNote = (line: BudgetLine): string => {
    const inc = line.increasePct ?? 0;
    const seats = 250 + (hashSeed(`${line.id}:seats`) % 1750); // 250–2,000 seats
    const next = Math.round(seats * (1 + inc));
    return `Usage-based: ${seats.toLocaleString()} → ${next.toLocaleString()} active licenses (+${Math.round(
        inc * 100
    )}%), projected from the H1 provisioning run-rate.`;
};
/** Justify a market / capped renewal increase, adapted to the vendor + rate.
 * Capped notes cite the contractual mechanism; market notes give an external
 * rationale (no contractual ceiling). Deterministic per line. */
const increaseNote = (line: BudgetLine): string => {
    const v = line.label;
    const x = Math.round((line.increasePct ?? 0) * 100);
    const pick = (arr: string[]) => arr[hashSeed(`${line.id}:incnote`) % arr.length];
    if (line.increaseKind === "capped") {
        const sec = `${8 + (hashSeed(`${line.id}:sec`) % 12)}.${
            1 + (hashSeed(`${line.id}:sub`) % 5)
        }`;
        return pick([
            `The ${v} agreement caps annual uplift at ${x}%, and the renewal reflects that contractual maximum.`,
            `Per Section ${sec} of the MSA, the renewal increase is contractually limited to ${x}%; budget reflects the cap being applied in full.`,
            `${v}'s renewal is index-linked to CPI-U, capped at ${x}%; we've budgeted to the cap as the conservative case.`,
            `The contract specifies the lesser of CPI movement or ${x}%; given current CPI trends, the ${x}% ceiling governs and is reflected here.`,
            `Uplift is fixed at ${x}% per the negotiated price-protection schedule, with no discretion for the vendor to exceed it.`,
            `The renewal applies the contractually agreed tiered escalator (${x}% this year), so the increase is known and non-negotiable for this period.`,
            `${v} pricing was held flat under the current term's price-lock; the modeled increase reflects the post-lock cap of ${x}%.`,
        ]);
    }
    const months = [9, 12, 18, 24][hashSeed(`${line.id}:mo`) % 4];
    return pick([
        `No contractual cap on ${v} — we've modeled a ${x}% increase in line with CPI-U to reflect expected list-price movement at renewal.`,
        `${v}'s renewal is open to repricing; we've budgeted ${x}% based on the vendor's published list-price increases over the trailing ${months} months.`,
        `${v} has raised list pricing year-over-year across comparable tiers; absent a cap, we've forecast a ${x}% increase as the likely renewal position.`,
        `Limited substitutes for ${v} weaken our renewal leverage; we've taken a conservative ${x}% increase to avoid under-budgeting in a low-competition category.`,
        `${v} invoices in EUR; we've modeled a ${x}% increase to account for unfavorable FX movement against the USD budget assumption.`,
        `Seat growth plus a per-user price uplift drives a blended ${x}% increase on ${v}; neither is capped under the current terms.`,
    ]);
};
const someNonNull = (xs: (number | null)[]) => xs.some((x) => x !== null);

interface VendorForecast extends TailVendor {
    forecast: number;
    increasePct: number;
}

/** Spread a tail category's trend across its vendors: each vendor gets a varied
 * (deterministic) increase rate, but the amounts still sum to the category total
 * (base × inc), so the rollup is unchanged. */
const vendorForecasts = (
    vendors: TailVendor[],
    inc: number
): VendorForecast[] => {
    const base = vendors.reduce((a, v) => a + v.spend, 0);
    const target = Math.round(base * inc); // total category increase $
    // Per-vendor raw weight: spend × a 0.4–1.8 factor keyed off the vendor id.
    const raw = vendors.map(
        (v) => v.spend * (0.4 + (hashSeed(`${v.id}:vinc`) % 140) / 100)
    );
    const rawSum = raw.reduce((a, b) => a + b, 0) || 1;
    let allocated = 0;
    return vendors.map((v, i) => {
        const amt =
            i === vendors.length - 1
                ? target - allocated
                : Math.round((raw[i] * target) / rawSum);
        allocated += amt;
        return {
            ...v,
            forecast: v.spend + amt,
            increasePct: v.spend ? amt / v.spend : 0,
        };
    });
};

interface Row {
    id: string;
    /** Source budget line (material & new-spend rows) — drives inline editing. */
    line?: BudgetLine;
    logoUrl?: string;
    /** Category icon fallback when there's no vendor logo (tail rows). */
    categoryKey?: CategoryKey;
    name: string;
    sub?: string;
    category?: string;
    closeQuarter?: string;
    budgeted: number;
    actual: number | null;
    current: number | null;
    /** Net of mitigation in forecast mode. */
    forecast: number;
    marginPct: number;
    /** Forecast breakdown for the budget tooltip (forecast mode, lines with a
     * current spend). */
    breakdown?: ForecastBreakdown;
}

interface ForecastBreakdown {
    current: number;
    increaseAmount: number;
    increasePct: number;
    mitigationAmount: number;
    total: number;
    /** New-contract variant — explained by annual contract value + savings. */
    isNewContract?: boolean;
    initial?: number;
    /** Manual correction folded into `total` (shown as its own row). */
    correction?: number;
}

interface Group {
    segment: Segment;
    label: string;
    tone: Tone;
    budgeted: number;
    actual: number | null;
    current: number | null;
    forecast: number;
    count: number;
    rows: Row[];
}

type Level = "total" | "section" | "line";
type ValueKey =
    | "budgeted"
    | "actual"
    | "delta"
    | "current"
    | "forecast"
    | "change";

interface Props {
    mode: "track" | "forecast";
}

export const OverviewView: React.FC<Props> = ({ mode }) => {
    const {
        workspace,
        dispatch,
        year,
        // Tail trend + corrections live in context so the Track tab reflects the
        // same budget edits.
        setTailIncrease,
        tailTrendFor: tailIncreaseFor,
        corrections,
        corrNotes,
        setCorrection,
    } = useBudget();
    const isForecast = mode === "forecast";
    const minYear = availableYears(workspace)[0];

    // Tail categories drilled open to show their per-vendor breakdown.
    const [openTail, setOpenTail] = useState<Record<string, boolean>>({});
    const toggleTail = (categoryId: string) =>
        setOpenTail((s) => ({ ...s, [categoryId]: !s[categoryId] }));
    const [playbookLine, setPlaybookLine] = useState<BudgetLine | null>(null);
    // Per-line review status (view state); defaults to pending.
    const [review, setReview] = useState<Record<string, ReviewState>>({});
    const reviewFor = (id: string): ReviewState => review[id] ?? "pending";
    const setReviewFor = (id: string, s: ReviewState) =>
        setReview((r) => ({ ...r, [id]: s }));
    // Overall budget review phase.
    const [phase, setPhase] = useState<Phase>("draft");
    const reviewEnabled = phase === "under_review";
    // Forecast opens fully expanded; Track keeps its default expansions.
    const [expanded, setExpanded] = useState<Record<Segment, boolean>>(
        isForecast ? ALL_EXPANDED : TRACK_EXPANDED
    );
    useEffect(() => {
        setExpanded(isForecast ? ALL_EXPANDED : TRACK_EXPANDED);
    }, [isForecast]);

    const realizedFraction = (y: number): number | null =>
        y < workspace.currentYear ? 1 : y === workspace.currentYear ? H1_FRACTION : null;

    const buildGroups = (y: number): Group[] => {
        const frac = realizedFraction(y);

        const byKey: Partial<Record<Segment, Group>> = {};
        for (const g of groupBySegment(getLines(workspace, y))) {
            const rows: Row[] = g.lines.map((l) => {
                const gross = l.planAmount.amount;
                // The budget bakes in the expected (median) savings; the savings
                // opportunity is shown in the Mitigation column. This net figure
                // is the budgeted line the Track tab tracks against.
                const mitigation =
                    isForecast && l.playbook ? l.playbook.savingsExpected.amount : 0;
                const net = gross - mitigation;
                const cur = l.currentSpend;
                const isNewContract =
                    l.segment === "new_spend" && Boolean(l.contractId);

                let breakdown: ForecastBreakdown | undefined;
                if (isForecast && isNewContract) {
                    breakdown = {
                        current: 0,
                        increaseAmount: 0,
                        increasePct: 0,
                        mitigationAmount: mitigation,
                        total: net,
                        isNewContract: true,
                        initial: gross,
                    };
                } else if (isForecast && cur !== undefined) {
                    breakdown = {
                        current: cur,
                        increaseAmount: gross - cur,
                        increasePct: l.increasePct ?? 0,
                        mitigationAmount: mitigation,
                        total: net,
                    };
                }

                return {
                    id: l.id,
                    line: l,
                    logoUrl: l.logoUrl,
                    name: l.label,
                    category: l.categoryName,
                    closeQuarter: l.closeQuarter,
                    budgeted: gross,
                    actual: frac === null ? null : l.actualAmount?.amount ?? 0,
                    current: cur ?? null,
                    forecast: net,
                    marginPct: 0,
                    breakdown,
                };
            });
            // Order rows by the quarter they land on; rows without a quarter
            // (under contract) keep their original order via the stable sort.
            // Manually-added new spend is exempt — it stays in insertion order at
            // the end of the segment, so a new entry appends rather than jumping
            // to wherever its quarter would sort.
            const isManual = (r: Row) =>
                r.line?.segment === "new_spend" && !r.line?.contractId;
            const fixed = rows.filter((r) => !isManual(r));
            const manual = rows.filter(isManual);
            fixed.sort(
                (a, b) => quarterIndexOf(a.closeQuarter) - quarterIndexOf(b.closeQuarter)
            );
            const orderedRows = [...fixed, ...manual];
            byKey[g.segment] = {
                segment: g.segment,
                label: g.label,
                tone: g.tone,
                budgeted: g.plan.amount,
                actual: frac === null ? null : sum(rows.map((r) => r.actual ?? 0)),
                current: someNonNull(rows.map((r) => r.current))
                    ? sum(rows.map((r) => r.current ?? 0))
                    : null,
                forecast: sum(rows.map((r) => r.forecast)),
                count: g.count,
                rows: orderedRows,
            };
        }

        // Tail pooled live. The per-category increase grows only the forecast —
        // the current spend is the present run-rate (the pooled base), untouched.
        const tailRows: Row[] = tailPoolByCategory(workspace).map((sc) => {
            const current = sc.base;
            const inc = tailIncreaseFor(sc.categoryId);
            const forecast = Math.round(sc.base * (1 + inc));
            const categoryKey = workspace.categories.find(
                (c) => c.id === sc.categoryId
            )?.key;
            return {
                id: sc.categoryId,
                categoryKey,
                name: sc.categoryName,
                sub: `${sc.vendorCount} vendors`,
                budgeted: forecast,
                actual: frac === null ? null : Math.round(forecast * frac),
                current,
                forecast,
                marginPct: 0,
                breakdown: isForecast
                    ? {
                          current,
                          increaseAmount: forecast - current,
                          increasePct: inc,
                          mitigationAmount: 0,
                          total: forecast,
                      }
                    : undefined,
            };
        });
        const tail: Group = {
            segment: "tail",
            label: "Tail spend",
            tone: "slate",
            budgeted: sum(tailRows.map((r) => r.budgeted)),
            actual: frac === null ? null : sum(tailRows.map((r) => r.actual ?? 0)),
            current: sum(tailRows.map((r) => r.current ?? 0)),
            forecast: sum(tailRows.map((r) => r.forecast)),
            count: tailRows.length,
            rows: tailRows,
        };

        return ORDER.map((seg) => (seg === "tail" ? tail : byKey[seg])).filter(
            (g): g is Group => Boolean(g) && (g as Group).count > 0
        );
    };

    const groups = buildGroups(year);
    const prior = !isForecast && year - 1 >= minYear ? buildGroups(year - 1) : null;
    const priorBudget = (seg: Segment) =>
        prior?.find((g) => g.segment === seg)?.budgeted;
    const priorTotal = prior ? sum(prior.map((g) => g.budgeted)) : undefined;

    // Sum of manual corrections across a set of rows (forecast mode only).
    const corrTotal = (rows: Row[]) => sum(rows.map((r) => corrections[r.id] ?? 0));
    const corrGrandTotal = sum(groups.map((g) => corrTotal(g.rows)));

    const totals = {
        budgeted: sum(groups.map((g) => g.budgeted)),
        actual: !isForecast ? sum(groups.map((g) => g.actual ?? 0)) : null,
        current: sum(groups.map((g) => g.current ?? 0)),
        forecast: sum(groups.map((g) => g.forecast)),
    };
    const totalLines = sum(groups.map((g) => g.count));

    const reviewCounts = { approved: 0, flagged: 0, pending: 0 };
    groups.forEach((g) =>
        g.rows.forEach((r) => {
            reviewCounts[reviewFor(r.id)] += 1;
        })
    );
    const allReviewed = totalLines > 0 && reviewCounts.pending === 0;

    // Bulk-set every line in a segment; the section dropdown shows the shared
    // value (or blank when the lines differ).
    const setSegmentReview = (g: Group, s: ReviewState) =>
        setReview((prev) => {
            const next = { ...prev };
            g.rows.forEach((r) => {
                next[r.id] = s;
            });
            return next;
        });
    const segmentReviewValue = (g: Group): ReviewState | undefined => {
        const states = g.rows.map((r) => reviewFor(r.id));
        return states.every((s) => s === states[0]) ? states[0] : undefined;
    };

    const valueCols: Array<{ key: ValueKey; label: string }> = isForecast
        ? [{ key: "forecast", label: "Target" }]
        : [
              { key: "budgeted", label: "Budgeted" },
              { key: "actual", label: "Actual" },
              { key: "delta", label: `vs FY${year - 1}` },
          ];

    const toggle = (seg: Segment) =>
        setExpanded((e) => ({ ...e, [seg]: !e[seg] }));

    const allOpen = groups.length > 0 && groups.every((g) => expanded[g.segment]);
    const toggleAll = () =>
        setExpanded((e) => {
            const next = { ...e };
            ORDER.forEach((seg) => {
                next[seg] = !allOpen;
            });
            return next;
        });

    const yoy = (cur: number, base: number | undefined) => {
        if (base === undefined) return <span className={classes.flat}>—</span>;
        if (base === 0)
            return <span className={classes.flat}>{cur > 0 ? "new" : "—"}</span>;
        const pct = (cur - base) / base;
        return (
            <span className={pct >= 0 ? classes.up : classes.down}>{formatPct(pct)}</span>
        );
    };

    // Always compact (M / K), never raw units.
    const fmt = (_level: Level, n: number) => formatCompact(usd(n));

    const valueCell = (
        level: Level,
        key: ValueKey,
        a: {
            budgeted: number;
            actual: number | null;
            current: number | null;
            forecast: number;
            marginPct?: number;
            prior?: number;
            breakdown?: ForecastBreakdown;
        }
    ): React.ReactNode => {
        switch (key) {
            case "budgeted":
                return fmt(level, a.budgeted);
            case "actual":
                return a.actual === null ? "—" : fmt(level, a.actual);
            case "current":
                return a.current === null ? "—" : fmt(level, a.current);
            case "forecast":
                if (level === "line" && a.breakdown) {
                    return (
                        <Tooltip
                            title={<BreakdownTip b={a.breakdown} />}
                            placement="left"
                        >
                            <span className={classes.tipNum}>
                                {formatCompact(usd(a.forecast))}
                            </span>
                        </Tooltip>
                    );
                }
                return fmt(level, a.forecast);
            case "change": {
                // Forecast vs current — on every row that has a current spend.
                if (!a.current) return null;
                return (
                    <span className={classes.changePct}>
                        {formatPct((a.forecast - a.current) / a.current)}
                    </span>
                );
            }
            case "delta":
                return level === "line" ? null : yoy(a.budgeted, a.prior);
        }
    };

    const valueCells = (
        level: Level,
        a: {
            budgeted: number;
            actual: number | null;
            current: number | null;
            forecast: number;
            marginPct?: number;
            prior?: number;
            breakdown?: ForecastBreakdown;
        }
    ) =>
        valueCols.map((c) => (
            <div
                key={c.key}
                className={
                    c.key === "delta" || c.key === "change"
                        ? classes.yoyCell
                        : classes.numCell
                }
            >
                {valueCell(level, c.key, a)}
            </div>
        ));

    // Pre-value tallies for segment + grand-total rows: current spend, the
    // baseline total (gross + corrections) with its change vs current, and the
    // mitigation total. Increase / correction stay blank (per-line edits).
    const baselineSum = (rows: Row[]) =>
        sum(rows.map((r) => r.budgeted + (corrections[r.id] ?? 0)));
    const baselineCells = (rows: Row[]) => {
        if (!isForecast) return null;
        const base = baselineSum(rows);
        const cur = sum(rows.map((r) => r.current ?? 0));
        const mit = sum(rows.map((r) => r.budgeted - r.forecast));
        return (
            <>
                <div className={classes.numCell}>
                    {cur > 0 ? formatCompact(usd(cur)) : ""}
                </div>
                <div className={classes.incCell} />
                <div className={classes.correctionCell} />
                <div className={classes.numCell}>{formatCompact(usd(base))}</div>
                <div className={classes.yoyCell}>
                    {cur > 0 ? (
                        <span className={classes.changePct}>
                            {formatPct((base - cur) / cur)}
                        </span>
                    ) : null}
                </div>
                <div className={classes.mitCell}>
                    {mit > 0 ? `~${formatCompact(usd(mit))}` : ""}
                </div>
            </>
        );
    };

    // A read-only contract/category line (material, tail, and new-contract rows).
    // `expand` makes the row a drill-down toggle (tail categories → vendors).
    const renderLineRow = (
        r: Row,
        segment: Segment,
        expand?: { open: boolean; onToggle: () => void }
    ) => (
        <div
            key={r.id}
            className={`${classes.row} ${classes.lineRow} ${
                expand ? classes.drillRow : ""
            }`}
        >
            <div
                className={classes.nameCell}
                onClick={expand?.onToggle}
                style={expand ? { cursor: "pointer" } : undefined}
            >
                {expand && (
                    <span className={classes.drillChev}>
                        {expand.open ? <DownOutlined /> : <RightOutlined />}
                    </span>
                )}
                {r.logoUrl ? (
                    <img
                        className={classes.logo}
                        src={r.logoUrl}
                        alt=""
                        loading="lazy"
                    />
                ) : r.categoryKey ? (
                    <span
                        className={classes.catIcon}
                        style={{ background: CATEGORY_COLOR[r.categoryKey] }}
                    >
                        {CATEGORY_ICON[r.categoryKey]}
                    </span>
                ) : null}
                <span className={classes.lineText}>
                    <span className={classes.lineName}>{r.name}</span>
                    {r.sub && <span className={classes.lineSub}>{r.sub}</span>}
                </span>
            </div>
            {segment === "new_spend" ? (
                // New contracts: same widgets as manual entry, but read-only.
                <>
                    <div className={classes.catCell}>
                        <Select
                            size="small"
                            bordered={false}
                            disabled
                            className={classes.editControl}
                            value={r.line?.categoryId}
                            options={workspace.categories.map((c) => ({
                                label: c.name,
                                value: c.id,
                            }))}
                        />
                    </div>
                    <div className={classes.termCell}>
                        <Select
                            size="small"
                            bordered={false}
                            disabled
                            className={classes.editControl}
                            value={r.closeQuarter || undefined}
                            options={quarterOptions(year).map((q) => ({
                                label: q,
                                value: q,
                            }))}
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className={classes.catCell}>{r.category ?? "—"}</div>
                    <div className={classes.termCell}>{r.closeQuarter ?? "—"}</div>
                </>
            )}
            {isForecast && (
                <div className={classes.numCell}>
                    {r.current === null ? "—" : formatCompact(usd(r.current))}
                </div>
            )}
            {isForecast && (
                <ForecastCells
                    line={r.line}
                    year={year}
                    onOpenPlaybook={setPlaybookLine}
                    name={r.name}
                    baseForecast={r.forecast}
                    breakdown={r.breakdown}
                    correction={corrections[r.id] ?? 0}
                    note={corrNotes[r.id] ?? ""}
                    onApply={(amount, reason) => setCorrection(r.id, amount, reason)}
                    tail={
                        segment === "tail"
                            ? {
                                  pct: tailIncreaseFor(r.id),
                                  onChange: (pct) => setTailIncrease(r.id, pct),
                                  note: tailTrendNote(
                                      parseInt(r.sub ?? "0", 10) || 0,
                                      tailIncreaseFor(r.id),
                                      r.id
                                  ),
                              }
                            : undefined
                    }
                />
            )}
            {valueCells(
                "line",
                isForecast
                    ? {
                          ...r,
                          forecast: r.forecast + (corrections[r.id] ?? 0),
                          breakdown: r.breakdown
                              ? {
                                    ...r.breakdown,
                                    correction: corrections[r.id] ?? 0,
                                    total:
                                        r.breakdown.total +
                                        (corrections[r.id] ?? 0),
                                }
                              : undefined,
                      }
                    : r
            )}
            {isForecast && (
                <ReviewTrio
                    id={r.id}
                    review={reviewFor(r.id)}
                    onReview={(s) => setReviewFor(r.id, s)}
                    reviewDisabled={!reviewEnabled}
                    showReview={!SEGMENT_REVIEW.includes(segment)}
                />
            )}
        </div>
    );

    // A vendor inside a drilled-open tail category — same columns, blank for the
    // ones that don't apply at the vendor level.
    const renderTailVendorRow = (v: VendorForecast) => {
        const current = v.spend;
        const forecast = v.forecast;
        return (
            <div
                key={`tv-${v.id}`}
                className={`${classes.row} ${classes.lineRow} ${classes.vendorRow}`}
            >
                <div className={classes.nameCell}>
                    <span className={classes.vendorName}>{v.vendorName}</span>
                </div>
                <div className={classes.catCell} />
                <div className={classes.termCell} />
                {isForecast && (
                    <div className={classes.numCell}>{formatCompact(usd(current))}</div>
                )}
                {isForecast && (
                    <>
                        <div className={classes.incCell} />
                        <div className={classes.correctionCell} />
                        <div className={classes.numCell} />
                        <div className={classes.yoyCell} />
                        <div className={classes.mitCell} />
                    </>
                )}
                {valueCells("line", {
                    budgeted: forecast,
                    actual: null,
                    current,
                    forecast,
                    breakdown: isForecast
                        ? {
                              current,
                              increaseAmount: forecast - current,
                              increasePct: v.increasePct,
                              mitigationAmount: 0,
                              total: forecast,
                          }
                        : undefined,
                })}
                {isForecast && <div className={classes.reviewCell} />}
            </div>
        );
    };

    return (
        <>
            <BudgetTabs />

            <div className={classes.fyHead}>
                <div className={classes.fyTitleRow}>
                    {isForecast && (
                        <div
                            className={classes.stage}
                            style={{ background: PHASE_COLOR[phase] }}
                        >
                            <Dropdown
                                trigger={["click"]}
                                overlay={
                                    <Menu
                                        selectedKeys={[phase]}
                                        onClick={({ key }) => setPhase(key as Phase)}
                                        items={[
                                            { key: "draft", label: PHASE_LABEL.draft },
                                            {
                                                key: "under_review",
                                                label: PHASE_LABEL.under_review,
                                            },
                                            {
                                                key: "approved",
                                                label: PHASE_LABEL.approved,
                                                disabled: !allReviewed,
                                            },
                                        ]}
                                    />
                                }
                            >
                                <button type="button" className={classes.stageName}>
                                    {PHASE_LABEL[phase]}
                                </button>
                            </Dropdown>
                            {NEXT_PHASE[phase] && (
                                <Tooltip
                                    title={
                                        phase === "under_review" && !allReviewed
                                            ? "Resolve all reviews to publish"
                                            : phase === "draft"
                                            ? "Move to review"
                                            : `Move to ${PHASE_LABEL[NEXT_PHASE[phase]!]}`
                                    }
                                >
                                    <button
                                        type="button"
                                        className={classes.stageArrow}
                                        onClick={() => {
                                            // Can't publish until nothing is pending.
                                            if (phase === "under_review" && !allReviewed)
                                                return;
                                            setPhase(NEXT_PHASE[phase]!);
                                        }}
                                    >
                                        <RightOutlined />
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {isForecast && phase === "under_review" && (
                        <ReviewBar counts={reviewCounts} />
                    )}
                </div>
            </div>

            {/* Column headers float above the table (no background). */}
            <div className={`${classes.row} ${classes.colHeaderRow}`}>
                <div className={classes.nameCell} />
                <div className={classes.catCell}>
                    <span className={classes.colLabel}>Category</span>
                </div>
                <div className={classes.termCell}>
                    <span className={classes.colLabel}>Quarter</span>
                </div>
                {isForecast && (
                    <div className={classes.numCell}>
                        <span className={classes.colLabel}>Current</span>
                    </div>
                )}
                {isForecast && (
                    <>
                        <div className={classes.incCell}>
                            <span className={classes.colLabel}>Increase</span>
                        </div>
                        <div className={classes.correctionCell}>
                            <span className={classes.colLabel}>Correction</span>
                        </div>
                        <div className={classes.numCell}>
                            <span className={classes.colLabel}>Baseline</span>
                        </div>
                        <div className={classes.yoyCell}>
                            <span className={classes.colLabel}>vs current</span>
                        </div>
                        <div className={classes.mitCell}>
                            <span className={classes.colLabel}>Opportunity</span>
                        </div>
                    </>
                )}
                {valueCols.map((c) => (
                    <div
                        key={c.key}
                        className={
                        c.key === "delta" || c.key === "change"
                            ? classes.yoyCell
                            : classes.numCell
                    }
                    >
                        <span className={classes.colLabel}>{c.label}</span>
                    </div>
                ))}
                {isForecast && (
                    <div className={classes.reviewCell}>
                        <span className={classes.colLabel}>Review</span>
                    </div>
                )}
            </div>

            <div className={classes.widget}>
                {/* Grand total across every segment — first row in the table. */}
                <div className={`${classes.row} ${classes.tallyRow}`}>
                    <div
                        className={`${classes.nameCell} ${classes.tallyName}`}
                        onClick={toggleAll}
                    >
                        <span className={classes.chev}>
                            {allOpen ? <DownOutlined /> : <RightOutlined />}
                        </span>
                        <span className={classes.tallyLabel}>All segments</span>
                        <span className={classes.count}>
                            {totalLines} {totalLines === 1 ? "line" : "lines"}
                        </span>
                    </div>
                    {/* Name spans the pre-value attribute columns so it keeps its
                        width as the table narrows. */}
                    {baselineCells(groups.flatMap((g) => g.rows))}
                    {valueCells(
                        "total",
                        isForecast
                            ? {
                                  ...totals,
                                  forecast: totals.forecast + corrGrandTotal,
                                  prior: priorTotal,
                              }
                            : { ...totals, prior: priorTotal }
                    )}
                    {isForecast && <div className={classes.reviewCell} />}
                </div>

                {groups.map((g) => {
                    const open = expanded[g.segment];
                    return (
                        <div key={g.segment} className={classes.section}>
                            <div
                                className={`${classes.row} ${classes.sectionRow}`}
                                onClick={() => toggle(g.segment)}
                            >
                                <div className={classes.nameCell}>
                                    <span className={classes.chev}>
                                        {open ? <DownOutlined /> : <RightOutlined />}
                                    </span>
                                    <span className={classes.segLabel}>{g.label}</span>
                                    <span className={classes.count}>
                                        {g.count} {g.count === 1 ? "line" : "lines"}
                                    </span>
                                </div>
                                {/* Name spans the pre-value columns (see tally row). */}
                                {baselineCells(g.rows)}
                                {valueCells(
                                    "section",
                                    isForecast
                                        ? {
                                              ...g,
                                              forecast: g.forecast + corrTotal(g.rows),
                                              prior: priorBudget(g.segment),
                                          }
                                        : { ...g, prior: priorBudget(g.segment) }
                                )}
                                {isForecast && (
                                    <SegmentReview
                                        value={segmentReviewValue(g)}
                                        onReview={(s) => setSegmentReview(g, s)}
                                        disabled={!reviewEnabled}
                                    />
                                )}
                            </div>

                            {/* New contracts first, then the editable manual /
                                initiative entries at the bottom. */}
                            {open &&
                                g.segment === "new_spend" &&
                                g.rows
                                    .filter((r) => r.line?.contractId)
                                    .map((r) => renderLineRow(r, "new_spend"))}

                            {open &&
                                g.segment === "new_spend" &&
                                g.rows
                                    .filter((r) => r.line && !r.line.contractId)
                                    .map((r) => (
                                        <NewSpendRow
                                            key={r.id}
                                            line={r.line!}
                                            year={year}
                                            valueCols={valueCols}
                                            isForecast={isForecast}
                                            review={reviewFor(r.id)}
                                            onReview={(s) => setReviewFor(r.id, s)}
                                            reviewDisabled={!reviewEnabled}
                                        />
                                    ))}

                            {open && g.segment === "new_spend" && (
                                <div
                                    className={`${classes.row} ${classes.lineRow} ${classes.addRow}`}
                                    onClick={() => dispatch({ type: "AddNewSpend", year })}
                                >
                                    <div className={classes.nameCell}>
                                        <span className={classes.addLabel}>
                                            <PlusOutlined /> Add new spend
                                        </span>
                                    </div>
                                </div>
                            )}

                            {open &&
                                g.segment === "tail" &&
                                g.rows.map((r) => (
                                    <React.Fragment key={r.id}>
                                        {renderLineRow(r, "tail", {
                                            open: !!openTail[r.id],
                                            onToggle: () => toggleTail(r.id),
                                        })}
                                        {openTail[r.id] &&
                                            vendorForecasts(
                                                tailVendorsForCategory(workspace, r.id),
                                                tailIncreaseFor(r.id)
                                            ).map((v) => renderTailVendorRow(v))}
                                    </React.Fragment>
                                ))}

                            {open &&
                                g.segment !== "new_spend" &&
                                g.segment !== "tail" &&
                                g.rows.map((r) => renderLineRow(r, g.segment))}
                        </div>
                    );
                })}
            </div>

            <PlaybookDrawer
                line={playbookLine}
                onClose={() => setPlaybookLine(null)}
            />
        </>
    );
};

/** An inline-editable new-spend line: name, category, close quarter, and budget
 * are edited in place; the trash icon removes it. Fully controlled off the
 * workspace — every change dispatches an EditNewSpend with the merged values. */
const NewSpendRow: React.FC<{
    line: BudgetLine;
    year: number;
    valueCols: Array<{ key: ValueKey; label: string }>;
    isForecast: boolean;
    review: ReviewState;
    onReview: (s: ReviewState) => void;
    reviewDisabled: boolean;
}> = ({ line, year, valueCols, isForecast, review, onReview, reviewDisabled }) => {
    const { workspace, dispatch } = useBudget();
    const categoryKey = workspace.categories.find((c) => c.id === line.categoryId)
        ?.key;

    const edit = (patch: {
        name?: string;
        categoryId?: BudgetLine["categoryId"];
        quarter?: string;
        amount?: number;
        confidence?: Confidence;
    }) =>
        dispatch({
            type: "EditNewSpend",
            year,
            lineId: line.id,
            name: patch.name ?? line.label,
            categoryId: patch.categoryId ?? line.categoryId,
            quarter: patch.quarter ?? line.closeQuarter ?? "",
            amount: patch.amount ?? line.planAmount.amount,
            confidence: patch.confidence ?? line.confidence ?? "low",
        });

    return (
        <div className={`${classes.row} ${classes.lineRow} ${classes.editRow}`}>
            <div className={classes.nameCell}>
                <button
                    type="button"
                    className={classes.deleteBtn}
                    aria-label="Delete new spend"
                    onClick={() =>
                        dispatch({ type: "DeleteNewSpend", year, lineId: line.id })
                    }
                >
                    <DeleteOutlined />
                </button>
                <span
                    className={classes.catIcon}
                    style={{
                        background: categoryKey
                            ? CATEGORY_COLOR[categoryKey]
                            : "#9ca3af",
                    }}
                >
                    {categoryKey ? CATEGORY_ICON[categoryKey] : <FileTextOutlined />}
                </span>
                <Input
                    bordered={false}
                    className={classes.editName}
                    value={line.label}
                    placeholder="Name…"
                    onChange={(e) => edit({ name: e.target.value })}
                />
            </div>
            <div className={classes.catCell}>
                <Select
                    size="small"
                    bordered={false}
                    className={classes.editControl}
                    value={line.categoryId}
                    onChange={(v) => edit({ categoryId: v })}
                    options={workspace.categories.map((c) => ({
                        label: c.name,
                        value: c.id,
                    }))}
                />
            </div>
            <div className={classes.termCell}>
                <Select
                    size="small"
                    bordered={false}
                    className={classes.editControl}
                    value={line.closeQuarter || undefined}
                    placeholder="Quarter"
                    onChange={(v) => edit({ quarter: v })}
                    options={quarterOptions(year).map((q) => ({ label: q, value: q }))}
                />
            </div>
            {isForecast && <div className={classes.numCell} />}
            {isForecast && (
                <>
                    <div className={classes.incCell}>—</div>
                    <div className={classes.correctionCell} />
                    <div className={classes.numCell} />
                    <div className={classes.yoyCell} />
                    <div className={classes.mitCell}>—</div>
                </>
            )}
            {valueCols.map((c) => (
                <div
                    key={c.key}
                    className={
                        c.key === "delta" || c.key === "change"
                            ? classes.yoyCell
                            : classes.numCell
                    }
                >
                    {c.key === "budgeted" || c.key === "forecast" ? (
                        <InputNumber
                            size="small"
                            bordered={false}
                            className={classes.editAmount}
                            value={Math.round(line.planAmount.amount / 1000)}
                            min={0}
                            step={10}
                            controls={false}
                            onChange={(v) =>
                                edit({ amount: (typeof v === "number" ? v : 0) * 1000 })
                            }
                            formatter={(v) => {
                                const k = Number(v);
                                if (v === undefined || !Number.isFinite(k))
                                    return "";
                                return Math.abs(k) >= 1000
                                    ? `$${(k / 1000)
                                          .toFixed(1)
                                          .replace(/\.0$/, "")}M`
                                    : `$${k}K`;
                            }}
                            parser={(v) => {
                                const s = (v ?? "").toString();
                                const num =
                                    parseFloat(s.replace(/[^0-9.]/g, "")) || 0;
                                return Math.round(/m/i.test(s) ? num * 1000 : num);
                            }}
                        />
                    ) : (
                        "—"
                    )}
                </div>
            ))}
            {isForecast && (
                <ReviewTrio
                    id={line.id}
                    review={review}
                    onReview={onReview}
                    reviewDisabled={reviewDisabled}
                    showReview
                />
            )}
        </div>
    );
};

/** Forecast-only cells: the FY increase (cap % + pill for capped renewals, an
 * editable % + pill for market / usage increases). */
const ForecastCells: React.FC<{
    line?: BudgetLine;
    year: number;
    onOpenPlaybook: (line: BudgetLine) => void;
    /** Present for tail rows — the editable per-category increase (view state). */
    tail?: { pct: number; onChange: (pct: number) => void; note: string };
    /** Manual forecast correction ($) + reason, edited via a popover dialog. */
    name: string;
    baseForecast: number;
    breakdown?: ForecastBreakdown;
    correction: number;
    note: string;
    onApply: (amount: number, note: string) => void;
}> = ({
    line,
    year,
    onOpenPlaybook,
    tail,
    name,
    baseForecast,
    breakdown,
    correction,
    note,
    onApply,
}) => {
    const { dispatch } = useBudget();
    const kind = line?.increaseKind;
    // Baseline budget = current + increase + correction (before mitigation).
    const baseline = baseForecast + (breakdown?.mitigationAmount ?? 0) + correction;

    return (
        <>
            <div className={classes.incCell}>
                {tail ? (
                    // Editable per-category tail trend %; justification tooltip
                    // hangs off the "Trend" pill.
                    <span className={classes.incWrap}>
                        <InputNumber
                            size="small"
                            min={0}
                            max={100}
                            value={+(tail.pct * 100).toFixed(1)}
                            onChange={(v) =>
                                tail.onChange((typeof v === "number" ? v : 0) / 100)
                            }
                            formatter={(v) => `${v}%`}
                            parser={(v) =>
                                Number((v ?? "").toString().replace("%", ""))
                            }
                            style={{ width: 64 }}
                        />
                        <Tooltip title={tail.note}>
                            <span className={`${classes.incPill} ${classes.incPillTip}`}>
                                Trend
                            </span>
                        </Tooltip>
                    </span>
                ) : kind === "usage" && line ? (
                    // Editable usage growth %; usage justification on the pill.
                    <span className={classes.incWrap}>
                        <InputNumber
                            size="small"
                            min={0}
                            max={100}
                            value={+((line.increasePct ?? 0) * 100).toFixed(1)}
                            onChange={(v) =>
                                dispatch({
                                    type: "SetIncrease",
                                    year,
                                    lineId: line.id,
                                    pct: (typeof v === "number" ? v : 0) / 100,
                                })
                            }
                            formatter={(v) => `${v}%`}
                            parser={(v) =>
                                Number((v ?? "").toString().replace("%", ""))
                            }
                            style={{ width: 64 }}
                        />
                        <Tooltip title={usageNote(line)}>
                            <span className={`${classes.incPill} ${classes.incPillTip}`}>
                                {INCREASE_PILL.usage}
                            </span>
                        </Tooltip>
                    </span>
                ) : kind && line ? (
                    <span className={classes.incWrap}>
                        <InputNumber
                            size="small"
                            min={0}
                            max={100}
                            value={+((line.increasePct ?? 0) * 100).toFixed(1)}
                            onChange={(v) =>
                                dispatch({
                                    type: "SetIncrease",
                                    year,
                                    lineId: line.id,
                                    pct: (typeof v === "number" ? v : 0) / 100,
                                })
                            }
                            formatter={(v) => `${v}%`}
                            parser={(v) =>
                                Number((v ?? "").toString().replace("%", ""))
                            }
                            style={{ width: 64 }}
                        />
                        <Tooltip title={increaseNote(line)}>
                            <span className={`${classes.incPill} ${classes.incPillTip}`}>
                                {INCREASE_PILL[kind]}
                            </span>
                        </Tooltip>
                    </span>
                ) : (
                    "—"
                )}
            </div>
            <div className={classes.correctionCell}>
                <CorrectionCell
                    name={name}
                    baseForecast={baseForecast}
                    breakdown={breakdown}
                    correction={correction}
                    note={note}
                    onApply={onApply}
                />
            </div>
            <div className={classes.numCell}>
                {breakdown ? (
                    <Tooltip
                        placement="left"
                        title={
                            <div className={classes.tip}>
                                {breakdown.isNewContract ? (
                                    <TipRow
                                        label="TCV / year"
                                        amount={formatCompact(
                                            usd(breakdown.initial ?? 0)
                                        )}
                                        pct=""
                                    />
                                ) : (
                                    <>
                                        <TipRow
                                            label="Current"
                                            amount={formatCompact(
                                                usd(breakdown.current)
                                            )}
                                            pct=""
                                        />
                                        <TipRow
                                            label="Increase"
                                            amount={signedCompact(
                                                breakdown.increaseAmount
                                            )}
                                            pct=""
                                        />
                                    </>
                                )}
                                {!!correction && (
                                    <TipRow
                                        label="Correction"
                                        amount={signedCompact(correction)}
                                        pct=""
                                    />
                                )}
                                <TipRow
                                    label="Baseline"
                                    amount={formatCompact(usd(baseline))}
                                    pct=""
                                    total
                                />
                            </div>
                        }
                    >
                        <span className={classes.tipNum}>
                            {formatCompact(usd(baseline))}
                        </span>
                    </Tooltip>
                ) : (
                    "—"
                )}
            </div>
            <div className={classes.yoyCell}>
                {breakdown && breakdown.current ? (
                    <span className={classes.changePct}>
                        {formatPct((baseline - breakdown.current) / breakdown.current)}
                    </span>
                ) : null}
            </div>
            <div className={classes.mitCell}>
                {line?.playbook ? (
                    <button
                        type="button"
                        className={classes.savingsBtn}
                        onClick={() => onOpenPlaybook(line)}
                    >
                        ~{formatCompact(line.playbook.savingsExpected)}
                    </button>
                ) : (
                    "—"
                )}
            </div>
        </>
    );
};

/** Correction column: a subtle trigger that opens a popover dialog showing the
 * forecast breakdown (like the tooltip), with inputs for an adjustment amount +
 * reason and a live adjusted total. Applying folds it into the forecast; the
 * reason shows as a tooltip on the chip. */
const CorrectionCell: React.FC<{
    name: string;
    baseForecast: number;
    breakdown?: ForecastBreakdown;
    correction: number;
    note: string;
    onApply: (amount: number, note: string) => void;
}> = ({ name, baseForecast, breakdown, correction, note, onApply }) => {
    const [open, setOpen] = useState(false);
    const [amt, setAmt] = useState(correction);
    const [reason, setReason] = useState(note);

    const onOpenChange = (o: boolean) => {
        if (o) {
            setAmt(correction);
            setReason(note);
        }
        setOpen(o);
    };

    const adjusted = baseForecast + amt;
    const rows: Array<[string, string]> = breakdown
        ? breakdown.isNewContract
            ? [
                  ["TCV / year", formatCompact(usd(breakdown.initial ?? 0))],
                  ...(breakdown.mitigationAmount > 0
                      ? ([["Savings", signedCompact(-breakdown.mitigationAmount)]] as [
                            string,
                            string
                        ][])
                      : []),
              ]
            : [
                  ["Current", formatCompact(usd(breakdown.current))],
                  ["Increase", signedCompact(breakdown.increaseAmount)],
                  ...(breakdown.mitigationAmount > 0
                      ? ([
                            ["Opportunity", signedCompact(-breakdown.mitigationAmount)],
                        ] as [string, string][])
                      : []),
              ]
        : [];

    const content = (
        <div className={classes.corrPop}>
            <div className={classes.corrPopTitle}>{name}</div>
            {rows.map(([label, value]) => (
                <div key={label} className={classes.corrPopRow}>
                    <span>{label}</span>
                    <span>{value}</span>
                </div>
            ))}
            <div className={`${classes.corrPopRow} ${classes.corrPopBase}`}>
                <span>Forecast</span>
                <span>{formatCompact(usd(baseForecast))}</span>
            </div>

            <div className={classes.corrField}>
                <span className={classes.corrFieldLabel}>Correction</span>
                <InputNumber
                    size="small"
                    autoFocus
                    controls={false}
                    className={classes.corrInput}
                    value={amt === 0 ? "" : amt / 1000}
                    onChange={(v) => setAmt((typeof v === "number" ? v : 0) * 1000)}
                    formatter={(v) => (v === "" || v === undefined ? "" : `${v}K`)}
                    parser={(v) =>
                        Number((v ?? "").toString().replace(/[^0-9-]/g, ""))
                    }
                />
            </div>
            <div className={classes.corrField}>
                <span className={classes.corrFieldLabel}>Reason</span>
                <Input
                    size="small"
                    value={reason}
                    placeholder="why"
                    onChange={(e) => setReason(e.target.value)}
                    onPressEnter={() => {
                        onApply(amt, reason.trim());
                        setOpen(false);
                    }}
                />
            </div>

            <div className={`${classes.corrPopRow} ${classes.corrAdjusted}`}>
                <span>Adjusted forecast</span>
                <span>{formatCompact(usd(adjusted))}</span>
            </div>

            <div className={classes.corrActions}>
                <Button size="small" type="text" onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                        onApply(amt, reason.trim());
                        setOpen(false);
                    }}
                >
                    Apply
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            open={open}
            onOpenChange={onOpenChange}
            trigger="click"
            placement="left"
            content={content}
        >
            {correction !== 0 ? (
                <Tooltip title={note || "No reason given"}>
                    <button type="button" className={classes.corrChip}>
                        {signedCompact(correction)}
                    </button>
                </Tooltip>
            ) : (
                <button type="button" className={classes.corrAdd}>
                    + Adjust
                </button>
            )}
        </Popover>
    );
};

/** Review cell, rendered after the value columns. */
const ReviewTrio: React.FC<{
    id: string;
    review: ReviewState;
    onReview: (s: ReviewState) => void;
    reviewDisabled: boolean;
    /** When false the row is reviewed at the segment level (header), not here. */
    showReview: boolean;
}> = ({ review, onReview, reviewDisabled, showReview }) => (
    <div className={classes.reviewCell}>
        {showReview && (
            <Select
                size="small"
                bordered={false}
                disabled={reviewDisabled}
                className={classes.editControl}
                value={review}
                onChange={onReview}
                options={REVIEW_OPTIONS}
            />
        )}
    </div>
);

/** Section-header review dropdown — bulk-sets the whole segment; shows the
 * shared value or "Mixed" when lines differ. */
const SegmentReview: React.FC<{
    value: ReviewState | undefined;
    onReview: (s: ReviewState) => void;
    disabled: boolean;
}> = ({ value, onReview, disabled }) => (
    <div className={classes.reviewCell} onClick={(e) => e.stopPropagation()}>
        <Select
            size="small"
            bordered={false}
            disabled={disabled}
            className={classes.editControl}
            value={value}
            placeholder="Mixed"
            onChange={onReview}
            options={REVIEW_OPTIONS}
        />
    </div>
);

/** Stacked status bar: approved (green) · flagged (orange) · pending (grey). */
const ReviewBar: React.FC<{
    counts: { approved: number; flagged: number; pending: number };
}> = ({ counts }) => {
    const total = counts.approved + counts.flagged + counts.pending || 1;
    const pct = (n: number) => `${(n / total) * 100}%`;
    return (
        <div className={classes.reviewBarWrap}>

            <div
                className={classes.reviewBar}
                title={`${counts.approved} approved · ${counts.flagged} flagged · ${counts.pending} pending`}
            >
                <span
                    className={classes.reviewApproved}
                    style={{ width: pct(counts.approved) }}
                />
                <span
                    className={classes.reviewFlagged}
                    style={{ width: pct(counts.flagged) }}
                />
                <span
                    className={classes.reviewPending}
                    style={{ width: pct(counts.pending) }}
                />
            </div>
            <span className={classes.reviewBarCounts}>
                {counts.approved} approved · {counts.flagged} flagged ·{" "}
                {counts.pending} pending
            </span>
        </div>
    );
};

const signedCompact = (n: number): string =>
    `${n >= 0 ? "+" : "−"}${formatCompact(usd(Math.abs(n)))}`;

/** Opportunity tooltip: baseline budget less mitigation = opportunity, with the
 * total compared back to the baseline. */
const BreakdownTip: React.FC<{ b: ForecastBreakdown }> = ({ b }) => {
    // b.total is the opportunity (net + correction); add mitigation back to get
    // the baseline budget it was derived from.
    const baseline = b.total + b.mitigationAmount;
    return (
        <div className={classes.tip}>
            <TipRow
                label="Baseline"
                amount={formatCompact(usd(baseline))}
                pct=""
            />
            {b.mitigationAmount > 0 && (
                <TipRow
                    label="Opportunity"
                    amount={signedCompact(-b.mitigationAmount)}
                    pct=""
                />
            )}
            <TipRow
                label="Target"
                amount={formatCompact(usd(b.total))}
                pct={baseline ? formatPct(-b.mitigationAmount / baseline) : ""}
                total
            />
        </div>
    );
};

const TipRow: React.FC<{
    label: string;
    amount: string;
    pct: string;
    total?: boolean;
}> = ({ label, amount, pct, total }) => (
    <div className={`${classes.tipRow} ${total ? classes.tipTotal : ""}`}>
        <span className={classes.tipLabel}>{label}</span>
        <span className={classes.tipAmount}>{amount}</span>
        <span className={classes.tipPct}>{pct}</span>
    </div>
);
